import React, { useState, useEffect } from "react";
import { Formik, useFormik } from "formik";
import * as Yup from "yup";
import {
  useSchoolContext,
  useGenderedSportList,
  useUserContext,
} from "../../../../contexts/ProviderProvider";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import { VENDORS } from "../../../../utilities/utils";
import {
  Autocomplete,
  Collapse,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import AthleteSearch from "../AthleteSearch";
import useNotification from "../../../../hooks/useNotification";
import {
  findProductMetadataMatching,
  getAllProductConfigurations,
} from "../../../../API/productConfiguration";
import { ManufacturingData } from "../ConfigureNewProduct";
import JerseyNumberSelector from "../JerseyNumberSelector";
import MatchingSimilarProducts from "../MatchingSimilarProducts";
import MultiSelect from "../../../common/MultiSelect";
import { MultilineChart } from "@mui/icons-material";
import { NewProductEQMConfig } from "../NewProductEQMConfig";
import { SelectModifier } from "../../../common/SelectModifier";
import { createTeamProducts } from "../../../../API/teamProducts";
import { getGeneralEQMAssetsByTypeForSku } from "../../../../API/equipmentManager";
import { EQM_TAG_VERSION_1_SUFFIX } from "../../../../constants/eqmTags";
import NewTeamProductEQMConfig from "./NewTeamProductEQMConfig";
import { getTeamSchoolAndSportList } from "../../../../API/teamGear";

const buildSKU = (color, unique, type) => {
  const format = (str) => {
    if (str && str !== "") {
      return str + " ";
    }
    if (str === undefined || str === null || str === "") {
      return "";
    }
  };
  if (color && type) {
    return `${color} ${format(unique)}${type}`;
  }
  return "";
};

const GetColorOptions = (productConfiguration, type) => {
  if (type && productConfiguration && productConfiguration[type]) {
    return Object.keys(productConfiguration[type]);
  }
  return [];
};

const GetConfiguration = (productConfiguration, type, color, onClick) => {
  const [selectedProductConfiguration, setSelectedProductConfiguration] =
    useState();
  const [hovering, setHovering] = useState();

  if (type && color) {
    if (productConfiguration[color] && productConfiguration[color][type]) {
      return (
        <div
          style={{
            display: "flex",
            direction: "row",
            gap: "1rem",
          }}
          key={type}
        >
          {productConfiguration[color][type].map((matching) => (
            <div
              style={{
                border:
                  selectedProductConfiguration?.product_config_id ===
                  matching.product_config_id
                    ? "solid green 2px"
                    : hovering?.product_config_id === matching.product_config_id
                    ? "solid gray 2px"
                    : "none",
              }}
              id={matching.product_config_id}
              onMouseOver={() => {
                setHovering(matching);
              }}
              onMouseLeave={() => {
                setHovering();
              }}
              onClick={() => {
                onClick(matching);
                setSelectedProductConfiguration(matching);
              }}
            >
              <ManufacturingData
                color={matching.color_reference}
                size={matching.size_range}
                style={matching.style_reference}
              />
            </div>
          ))}
        </div>
      );
    }
    return undefined;
  }
  return undefined;
};

const ErrorText = ({ message }) => {
  return <p style={{ color: "#f44336", fontSize: "0.75rem" }}>{message}</p>;
};

const filterVendors = (inputProdType) => {
  var TP = ["Coaster", "Jersey", "Blanket", "Tumbler", "Number Necklace"];
  var full = VENDORS;
  if (TP.includes(inputProdType)) {
    return full.filter((ven) => ven != "Influxer Merch");
  }

  return full;
};

const CreateTeamProductForm = () => {
  const [universities, setUniversities] = useState([]);
  const [productConfiguration, setProductConfigurations] = useState();
  const [similarProducts, setSimilarProducts] = useState([]);
  const [typeCheck, setTypeCheck] = useState();
  const [genderedSportList, setGenderedSportList] = useState([]);
  const [schoolAndSportLoading, setSchoolAndSportLoading] = useState(false);
  // FOR EQM CONFIG
  const [rows, setRows] = useState([]);
  const [eqmConfig, setEqmConfig] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [displayEQMConfigTab, setDisplayEQMConfigTab] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [generalAssetByType, setGeneralAssetByType] = useState({});
  const [fontOptions, setFontOptions] = useState([]);
  const [compositeOptions, setCompositeOptions] = useState([]);
  const [backIconLinkOptions, setBackIconLinkOptions] = useState([]);
  const [frontIconLinkOptions, setFrontIconLinkOptions] = useState([]);
  const [templateLinkOptions, setTemplateLinkOptions] = useState([]);
  const [jerseyTemplateLinkOptions, setJerseyTemplateLinkOptions] = useState(
    []
  );

  const user = useUserContext();
  const notify = useNotification();

  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    skuName: Yup.string(),
    color: Yup.string()
      .required("Color is required")
      .test(
        "verify value in list",
        "select a value from the dropdown",
        (value) => {
          return Object.keys(productConfiguration).includes(value);
        }
      ),
    unique: Yup.string(),
    type: Yup.string().required("Type is required"),
    university: Yup.array().test(
      "verify not empty array",
      "you must select some schools, but no more than 4... for now",
      (value) =>
        value && Array.isArray(value) && value.length > 0 && value.length < 5
    ),
    sport: Yup.array().test(
      "verify not empty array",
      "you must select some sports, or all of them",
      (value) => value && Array.isArray(value) && value.length > 0
    ),
    approvalStatus: Yup.boolean().oneOf(
      [true, false],
      "Approval Status is required"
    ),
    vendor: Yup.string().required("Vendor is required"),
    colorReference: Yup.string(),
    styleReference: Yup.string(),
    sizeRange: Yup.string(),
  });

  const handleSubmit = async (obj, { resetForm }) => {
    // Validate all universities' configurations
    if (displayEQMConfigTab) {
      if (!validateForm()) {
        setErrorMessage("* Missing required fields");
        return;
      }
    }

    let formObj = { ...obj };
    if (
      formObj.university.includes("All") ||
      formObj.university === undefined ||
      formObj.university.length === 0
    ) {
      delete formObj.university;
    }
    if (
      formObj.sport.includes("All") ||
      formObj.sport === undefined ||
      formObj.sport.length === 0
    ) {
      delete formObj.sport;
    } else {
      formObj.genderedSport = formObj.sport;
      delete formObj.sport;
    }

    formObj.config = eqmConfig || {};

    console.log("formObject to pass to GS: ", formObj);

    setLoading(true);

    try {
      const filesToUpload = [];
      const updatedConfig = formObj.config;

      for (const university in updatedConfig) {
        const uniConfig = updatedConfig[university];

        for (const key in uniConfig) {
          const fileDetail = uniConfig[key];
          if (fileDetail instanceof File) {
            const fieldName = `file_${university}_${key}_${
              fileDetail?.name || ""
            }`.replace(/\s+/g, "_");
            filesToUpload.push({ fieldName, file: fileDetail });
            updatedConfig[university][key] = fieldName;
          }
        }
      }

      formObj.config = updatedConfig;

      console.log(`formObj`, formObj);

      const response = await createTeamProducts(formObj, filesToUpload, user);
      console.log("response from team product creation: ", response);
      if (response.data.code === 200) {
        notify({ variant: "success", msg: "product creation compelete" });
      } else {
        notify("something went wrong. status code: ", response.data.code);
      }
    } catch (error) {
      console.error("error creating products:", error);
      const errMessage =
        error?.response?.data?.code === 409
          ? "Team Product already exists"
          : error?.response?.data?.err || "Failed to create product";
      notify(errMessage);
    } finally {
      setLoading(false);
      resetForm();
    }
  };

  const formik = useFormik({
    initialValues: {
      skuName: "",
      color: "",
      type: "",
      unique: "",
      university: [],
      sport: [],
      approvalStatus: false,
      vendor: "",
      sizeRange: "",
      styleReference: "",
      colorReference: "",
      config: {},
      group: "Team",
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    const fetchAssets = () => {
      getGeneralEQMAssetsByTypeForSku(
        user,
        formik.values.university,
        formik.values.type,
        { versionedOnly: true }
      )
        .then((res) => {
          setGeneralAssetByType(res.data.obj);

          // Set options for different asset types
          const assetTypes = {
            "Font File": setFontOptions,
            "Back Asset Icon File": setBackIconLinkOptions,
            "Front Asset Icon File": setFrontIconLinkOptions,
            "Mockup Template File": setTemplateLinkOptions,
            "Composite Font File": setCompositeOptions,
            "Jersey Art File": setJerseyTemplateLinkOptions,
          };

          Object.entries(assetTypes).forEach(([assetType, setter]) => {
            if (assetType in res.data.obj) {
              const keys = Object.values(res.data.obj[assetType]);
              if (
                assetType === "Composite Font File" ||
                assetType === "Jersey Art File"
              ) {
                setter(keys.filter((key) =>
                  key.tags.endsWith(EQM_TAG_VERSION_1_SUFFIX)
                ));
              } else {
                setter(keys);
              }
            } else {
              setter([]);
            }
          });
        })
        .catch((err) => notify("Failed to get assets: " + err));
    };

    fetchAssets();
    const interval = setInterval(fetchAssets, 10000);
    return () => clearInterval(interval);
  }, [user, notify, formik.values.university, formik.values.type]);

  useEffect(() => {
    getAllProductConfigurations(user)
      .then((response) => {
        setProductConfigurations(response.data);
        setLoading(false);
      })
      .catch((err) => {
        notify("Failed to get product configurations");
        setLoading(false);
      });
  }, [user, notify]);

  useEffect(() => {
    const controller = new AbortController();

    const config = {
      sku_name: formik.values.skuName,
      color: formik.values.color,
      type: formik.values.type,
      university: formik.values.university,
      style: formik.values.styleReference,
      color_reference: formik.values.colorReference,
      size_range: formik.values.sizeRange,
      sport: formik.values.sport,
      // approval_status: formik.values.approvalStatus ? 1 : null,
      vendor: formik.values.vendor,
      group: formik.values.group,
    };

    // console.log("pre", config);
    for (var field in config) {
      if (Object.prototype.hasOwnProperty.call(config, field)) {
        if (
          config[field] === undefined ||
          config[field] === null ||
          config[field] === ""
        ) {
          delete config[field];
        }
      }
    }

    // console.log("post", config);

    findProductMetadataMatching(config, controller.signal, user)
      .then((resp) => {
        setSimilarProducts(resp.data);
        // console.log(resp.data);
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          notify(e.response?.data);
        }
      });

    return () => {
      controller.abort();
    };
  }, [formik.values]);

  const validateForm = () => {
    let isValid = true;
    const invalidUniversities = new Set();

    const formikUniversities = formik.values.university || [];

    if (formikUniversities.length === 0) {
      isValid = false;
    } else {
      for (const university of formikUniversities) {
        if (!eqmConfig || !eqmConfig[university]) {
          // console.log(`University ${university} not in eqmConfig`);
          invalidUniversities.add(university);
          isValid = false;
        } else {
          const universityConfig = eqmConfig[university];
          for (const row of rows || []) {
            if (row.type === "Boolean") {
              const value = universityConfig[row.name];
              if (value !== undefined && value === true) {
                const associatedValue = universityConfig[row.associatedRow];
                if (associatedValue === undefined || associatedValue === null) {
                  invalidUniversities.add(university);
                  isValid = false;
                  break;
                }
              }
            }
            // vice-versa check for image and bool
            // else {
            //     const value = universityConfig[row.name]
            //     if (value !== undefined && value !== null) {
            //         const associatedValue = universityConfig[row.associatedRow]
            //         if (associatedValue === undefined || associatedValue !== true) {
            //             invalidUniversities.add(university);
            //             isValid = false;
            //             break;
            //         }
            //     }
            // }
          }
        }
      }
    }

    if (!isValid) {
      setErrorMessage("Please fill out all required fields.");
      setExpanded((prev) => {
        const newExpanded = { ...prev };
        invalidUniversities.forEach((university) => {
          newExpanded[university] = true;
        });
        return newExpanded;
      });
    } else {
      setErrorMessage("");
      setExpanded({});
    }

    return isValid;
  };

  useEffect(() => {
    setEqmConfig({});
    setDisplayEQMConfigTab(true);
    // setDisplayEQMConfigTab(
    //     formik.values.type && formik.values.type !== "Performance Tee" && formik.values.type !== "Performance Long Sleeve"
    // );
  }, [formik.values.type]);

  useEffect(() => {
    const fetchUniversitiesAndSports = async () => {
      try {
        setSchoolAndSportLoading(true);
        const response = await getTeamSchoolAndSportList(user);
        console.log("response from GS with school and sport: ", response);
        if (response?.data?.code === 200) {
          setUniversities(response?.data?.obj?.school || []);
          setGenderedSportList(response?.data?.obj?.sport || []);
        } else {
          notify("Failed to get Team School and Sport list from response");
        }
      } catch (error) {
        console.error("error fetching school and Sport list: ", error);
        notify("Failed to get Team School and Sport list");
      } finally {
        setSchoolAndSportLoading(false);
      }
    };

    fetchUniversitiesAndSports();
  }, []);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignContent: "center",
      }}
    >
      {loading && <LinearProgress />}
      <form onSubmit={formik.handleSubmit}>
        <TextField
          id="group"
          name="group"
          label="Group"
          variant="outlined"
          style={{ marginTop: "5vh" }}
          fullWidth
          disabled
          value={formik.values.group}
          error={formik.touched.group && Boolean(formik.errors.group)}
          helperText={formik.touched.group && formik.errors.group}
        />
        <MultiSelect
          label="University"
          style={{ paddingTop: "10px" }}
          value={formik.values.university}
          onChange={(newValue) => {
            formik.setFieldValue("university", newValue);
          }}
          between={"OR"}
          onBlur={() => {}}
          options={[...universities]}
          getOptionLabel={(option) => option}
        />
        {schoolAndSportLoading && <LinearProgress />}
        {formik.touched.university && formik.errors.university && (
          <ErrorText message={formik.errors.university} />
        )}
        <MultiSelect
          label="Sport"
          style={{ paddingTop: "10px" }}
          value={formik.values.sport}
          onChange={(newValue) => {
            formik.setFieldValue("sport", newValue);
          }}
          between={"OR"}
          onBlur={formik.handleBlur}
          options={genderedSportList}
          getOptionLabel={(option) => option}
          allButton
        />
        {schoolAndSportLoading && <LinearProgress />}
        {formik.touched.sport && formik.errors.sport && (
          <ErrorText message={formik.errors.sport} />
        )}

        <Autocomplete
          style={{ paddingTop: "15px" }}
          value={formik.values.color}
          onChange={(event, newValue) => {
            formik.setFieldValue("color", newValue);
            formik.setFieldValue(
              "skuName",
              buildSKU(newValue, formik.values.unique, formik.values.type)
            );
          }}
          onBlur={(a) => {
            formik.handleBlur(a);
            formik.setTouched({ ...formik.touched, color: true });
            formik.setFieldValue("color", a.target.value);
            formik.setFieldValue(
              "skuName",
              buildSKU(a.target.value, formik.values.unique, formik.values.type)
            );
          }}
          options={
            productConfiguration ? Object.keys(productConfiguration) : []
          }
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Color"
              variant="outlined"
              style={{ width: "100%" }}
              InputProps={{
                ...params.InputProps,
              }}
              type="text"
              error={formik.touched.color && Boolean(formik.errors.color)}
              helperText={formik.touched.color && formik.errors.color}
            />
          )}
        />
        <TextField
          id="unique"
          name="unique"
          label="Unique"
          variant="outlined"
          style={{ marginTop: "10px" }}
          fullWidth
          onChange={(e, newValue) => {
            formik.setFieldValue("unique", newValue);
            formik.setFieldValue(
              "skuName",
              buildSKU(formik.values.color, newValue, formik.values.type)
            );
          }}
          onBlur={(e, f) => {
            formik.handleBlur(e, f);
            formik.setFieldValue("unique", e.target.value);
            formik.setFieldValue(
              "skuName",
              buildSKU(formik.values.color, e.target.value, formik.values.type)
            );
          }}
          value={formik.values.unique}
          error={formik.touched.unique && Boolean(formik.errors.unique)}
          helperText={formik.touched.unique && formik.errors.unique}
        />
        <Autocomplete
          style={{ paddingTop: "15px" }}
          value={formik.values.type}
          onChange={(event, newValue) => {
            formik.setFieldValue("type", newValue);
            formik.setFieldValue(
              "skuName",
              buildSKU(formik.values.color, formik.values.unique, newValue)
            );

            setTypeCheck(false);
          }}
          onBlur={(a) => {
            formik.handleBlur(a);
            formik.setTouched({ ...formik.touched, type: true });
            formik.setFieldValue("type", a.target.value);
            formik.setFieldValue(
              "skuName",
              buildSKU(
                formik.values.color,
                formik.values.unique,
                a.target.value
              )
            );
          }}
          options={GetColorOptions(productConfiguration, formik.values.color)}
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type"
              variant="outlined"
              style={{ width: "100%" }}
              InputProps={{
                ...params.InputProps,
              }}
              type="text"
              error={formik.touched.type && Boolean(formik.errors.type)}
              helperText={formik.touched.type && formik.errors.type}
            />
          )}
        />
        <TextField
          id="skuName"
          name="skuName"
          label="SKU Name"
          variant="outlined"
          disabled
          style={{ marginTop: "20px" }}
          fullWidth
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.skuName}
          error={formik.touched.skuName && Boolean(formik.errors.skuName)}
          helperText={formik.touched.skuName && formik.errors.skuName}
        />
        {GetConfiguration(
          productConfiguration,
          formik.values.type,
          formik.values.color,
          (productConfiguration) => {
            formik.setFieldValue(
              "colorReference",
              productConfiguration.color_reference
            );
            formik.setFieldValue("sizeRange", productConfiguration.size_range);
            formik.setFieldValue(
              "styleReference",
              productConfiguration.style_reference
            );
            formik.setFieldTouched("sizeRange", true);
            formik.setFieldTouched("styleReference", true);
            formik.setFieldTouched("colorReference", true);
            formik.setFieldError("sizeRange", undefined);
            formik.setFieldError("styleReference", undefined);
            formik.setFieldError("colorReference", undefined);
          }
        )}
        <Autocomplete
          style={{ paddingTop: "15px" }}
          value={formik.values.vendor}
          onChange={(event, newValue) => {
            formik.setFieldValue("vendor", newValue);
          }}
          onBlur={(a) => {
            formik.handleBlur(a);
            formik.setTouched({ ...formik.touched, vendor: true });
            formik.setFieldValue("vendor", a.target.value);
          }}
          options={filterVendors(formik.values.type)}
          getOptionLabel={(option) => option}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              variant="outlined"
              style={{ width: "100%" }}
              InputProps={{
                ...params.InputProps,
              }}
              type="text"
              error={formik.touched.vendor && Boolean(formik.errors.vendor)}
              helperText={formik.touched.vendor && formik.errors.vendor}
            />
          )}
        />
        <Tooltip
          title="Check this box if there's a school logo or mark on this item"
          arrow
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            },
          }}
        >
          <FormControlLabel
            style={{ marginTop: "20px" }}
            control={
              <Checkbox
                id="approvalStatus"
                name="approvalStatus"
                checked={formik.values.approvalStatus}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            }
            label="Royalty Bearing"
          />
        </Tooltip>
        <br />
        {formik.touched.approvalStatus && formik.errors.approvalStatus && (
          <ErrorText message={formik.errors.approvalStatus} />
        )}
        {formik.touched.colorReference && formik.errors.colorReference && (
          <ErrorText message={formik.errors.colorReference} />
        )}
        {formik.touched.sizeRange && formik.errors.sizeRange && (
          <ErrorText message={formik.errors.sizeRange} />
        )}
        {formik.touched.styleReference && formik.errors.styleReference && (
          <ErrorText message={formik.errors.styleReference} />
        )}
        {similarProducts.length > 0 && (
          <div style={{ margin: "10px 0px 15px 0px" }}>
            <MatchingSimilarProducts similarProducts={similarProducts} />
          </div>
        )}
        {/* Team EQM Congfig add here */}
        {displayEQMConfigTab && formik.values.university.length > 0 ? (
          <NewTeamProductEQMConfig
            rows={rows}
            setRows={setRows}
            eqmConfig={eqmConfig}
            setEQMConfig={setEqmConfig}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
            universities={formik.values.university}
            expanded={expanded}
            setExpanded={setExpanded}
            fontOptions={fontOptions}
            backIconLinkOptions={backIconLinkOptions}
            frontIconLinkOptions={frontIconLinkOptions}
            templateLinkOptions={
              formik.values.type == "Jersey"
                ? jerseyTemplateLinkOptions
                : templateLinkOptions
            }
            compositeOptions={compositeOptions}
            generalAssetByType={generalAssetByType}
            selectedSkuType={formik.values.type}
          />
        ) : null}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          style={{ marginRight: "1rem" }}
        >
          Submit
        </Button>
        <Typography sx={{ color: "error.main" }}>
          Warning, this will create this product for ALL athletes that the,
          school, sport, athlete, jersey number filter applies to.
        </Typography>
      </form>
    </div>
  );
};

export default CreateTeamProductForm;
